import { CardContent, Typography } from '@mui/material';
import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { Button, ButtonProps } from '@ui/buttons';
import { PlanData } from '@shared/models/paywall.model';
import { usePaywallStore } from '../../paywall.store';
import { useAsyncErrorHandler } from '@client/hooks/use-error-handler.hooks';
import amplitudeLog from '@client/amplitude';
import { AMP_PAYWALL_SUBMIT } from '@shared/constants';

type SubmitButtonProps = {
  color?: ButtonProps['color'];
  text: string;
};

export const PaywallCardSubmit = ({
  color = 'primary',
  text,
  ...data
}: SubmitButtonProps & PlanData) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setPlanData, closeDialog } = usePaywallStore();
  const asyncErrorHandler = useAsyncErrorHandler<boolean>();

  const handleClick = useCallback(async () => {
    setIsSubmitting(true);
    const isSuccess = await asyncErrorHandler(submitPlan(data));

    if (isSuccess) {
      amplitudeLog(AMP_PAYWALL_SUBMIT, data);
      setPlanData(data);
      closeDialog();
    }
    setIsSubmitting(false);
  }, [data, setPlanData, closeDialog, asyncErrorHandler]);

  return (
    <CardContent sx={{ paddingBottom: 2 }}>
      <Button
        fullWidth
        variant="contained"
        color={color}
        disabled={isSubmitting}
        onClick={handleClick}
      >
        <Typography
          variant="button"
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            fontSize: 14,
          }}
        >
          {text}
        </Typography>
      </Button>
    </CardContent>
  );
};

const submitPlan = async ({
  plan,
  billedAnnually,
  activeProjects,
}: PlanData) => {
  try {
    const data: PlanData =
      plan === 'pro' ? { plan, billedAnnually, activeProjects } : { plan };
    await axios.post('/paywall/submit-plan', data);
    return true;
  } catch {
    throw new Error('Failed to submit plan data');
  }
};
